/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FavoritesIconEnum, FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconReactionLoveFilled,
	homefieldIconReactionLoveOutlined
} from '@dsg-tech/homefield-angular-icons';
import { MyCustomizations, PersonalizationState } from '@shared/data-access/interfaces';

import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { WishListService } from '@shared/favorites/data-access/services';

@Component({
	selector: 'pdp-add-to-cart-components-add-to-list-button',
	imports: [HmfButtonComponent, HomefieldIconLibraryModule],
	templateUrl: './add-to-list-button.component.html',
	styleUrl: './add-to-list-button.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddToListButtonComponent {
	private readonly wishlistService = inject(WishListService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	readonly customAttributes = input<MyCustomizations | null>(null);
	readonly listProductPersonalization = input<PersonalizationState | Record<string, never>>({});
	readonly ecode = input.required<string>();
	readonly resolvedSku = input<string>('');
	readonly isSingleSkuProduct = input<boolean>(false);
	readonly isAuthenticated = input<boolean>(false);

	protected FavoritesIconEnum = FavoritesIconEnum;

	readonly isSkuAvailable = computed(() => !!this.resolvedSku() && !this.isSingleSkuProduct());

	readonly productsInStore = this.wishlistService.productsInStoreSignal();

	readonly actionInProgress = this.wishlistService.actionInProgress;

	readonly productIsFavorited = computed(() => {
		let skuOrEcode = this.ecode();
		if (this.isSkuAvailable()) {
			skuOrEcode = this.resolvedSku() || '';
		}
		if (this.customAttributes()?.MyCustomizerMap.designId) {
			skuOrEcode += `-MC-${this.customAttributes().MyCustomizerMap.designId}`;
		}

		return !!this.productsInStore().get(skuOrEcode);
	});

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconReactionLoveFilled,
			homefieldIconReactionLoveOutlined
		]);
	}

	iconClicked() {
		const stampedSku = this.wishlistService.determineSkuStamp(
			this.resolvedSku(),
			this.listProductPersonalization(),
			this.customAttributes()
		);
		const productAttributes = this.wishlistService.getProductAttributes(
			this.listProductPersonalization(),
			this.customAttributes()
		);

		this.wishlistService
			.determineFavoritesAction({
				isAuthenticated: this.isAuthenticated(),
				ecode: this.ecode(),
				isSingleSkuProduct: this.isSingleSkuProduct(),
				sku: stampedSku,
				productAttributes,
				productIsFavorited: this.productIsFavorited(),
				favoritesLocation: FavoritesLocationEnum.PDP,
				productsInStore: this.productsInStore()
			})
			.subscribe();
	}
}
